import { FormControl, FormHelperText, FormLabel, Typography, Box } from '@material-ui/core';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  section: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  mainLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    fontSize: '0.875rem',
  },
  optionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  defaultVpcOption: {
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.primary.main}`,
  },
  mongodbSection: {
    marginTop: theme.spacing(1.5),
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.secondary.main}`,
  },
  mongodbLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  mongodbOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(2),
  },
}));

/** API lambdas (phx-api-v2); step-function managed lambdas use {@link DEFAULT_VPC_ACCESS_STEP_FUNCTION}. */
export const DEFAULT_VPC_ACCESS_PHX_API = '{{env_name}}-phx-vpc-endpoints-sg';
/** Step Function managed_lambdas default VPC security group id. */
export const DEFAULT_VPC_ACCESS_STEP_FUNCTION = 'lexipub_lambdas_sg';

const MONGODB_CLUSTERS = [
  {
    label: 'Lexipub',
    value: 'atlas-pub-manuals-{{env_name}}-privatelink-clients-sg',
  },
  {
    label: 'Lexiverse',
    value: 'atlas-lexiverse-manuals-{{env_name}}-privatelink-clients-sg',
  },
];

interface SecurityGroupsSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  helperText?: string;
  /**
   * Security group id toggled by "default vpc access" (must match Terraform for that stack type).
   * Defaults to phx API lambdas (`{{env_name}}-phx-vpc-endpoints-sg`); use
   * `lexipub_lambdas_sg` for Step Function managed_lambdas.
   */
  defaultVpcAccessSgId?: string;
  /** Disable all controls (e.g. LMI lambdas don't support VPC settings / security groups). */
  disabled?: boolean;
}

export const SecurityGroupsSelector = ({
  value,
  onChange,
  label = 'Security Groups',
  helperText,
  defaultVpcAccessSgId = DEFAULT_VPC_ACCESS_PHX_API,
  disabled = false,
}: SecurityGroupsSelectorProps) => {
  const classes = useStyles();
  const selectedValues = value || [];

  const hasDefaultVpcAccess = selectedValues.includes(defaultVpcAccessSgId);
  const mongodbValues = selectedValues.filter(v => v !== defaultVpcAccessSgId);

  const handleDefaultVpcToggle = (checked: boolean) => {
    if (checked) {
      onChange([...selectedValues, defaultVpcAccessSgId]);
    } else {
      onChange(selectedValues.filter(v => v !== defaultVpcAccessSgId));
    }
  };

  const handleMongoDbToggle = (clusterValue: string) => {
    const otherValues = selectedValues.filter(
      v => v !== clusterValue && v !== defaultVpcAccessSgId,
    );
    if (mongodbValues.includes(clusterValue)) {
      onChange([...otherValues, ...(hasDefaultVpcAccess ? [defaultVpcAccessSgId] : [])]);
    } else {
      onChange([
        ...otherValues,
        clusterValue,
        ...(hasDefaultVpcAccess ? [defaultVpcAccessSgId] : []),
      ]);
    }
  };

  return (
    <Box className={classes.section}>
      <FormControl fullWidth>
        <FormLabel component="legend" className={classes.mainLabel}>
          {label}
        </FormLabel>

        <div className={classes.optionContainer}>
          {/* Default VPC Access */}
          <Box className={classes.defaultVpcOption}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasDefaultVpcAccess}
                  onChange={e => handleDefaultVpcToggle(e.target.checked)}
                  color="primary"
                  size="small"
                  disabled={disabled}
                />
              }
              label={
                <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                  default vpc access
                </Typography>
              }
            />
          </Box>

          {/* MongoDB Subsection */}
          <Box className={classes.mongodbSection}>
            <Typography className={classes.mongodbLabel}>MongoDB</Typography>
            <div
              className={classes.mongodbOptions}
              style={{ display: 'flex', flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}
            >
              {MONGODB_CLUSTERS.map(cluster => (
                <FormControlLabel
                  key={cluster.value}
                  control={
                    <Checkbox
                      checked={mongodbValues.includes(cluster.value)}
                      onChange={() => handleMongoDbToggle(cluster.value)}
                      color="primary"
                      size="small"
                      disabled={disabled}
                    />
                  }
                  label={
                    <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                      {cluster.label}
                    </Typography>
                  }
                />
              ))}
            </div>
          </Box>
        </div>

        {helperText && <FormHelperText style={{ marginTop: 8 }}>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
